import { MINUTE_HEIGHT_PX } from 'constants/hour';
import type { DateTime } from 'luxon';
import React, { useCallback, useMemo } from 'react';
import { TimelineStyleTypes } from 'redux/modules/timeline.types';
import { Hour } from 'routes/supercalendar/components/calendar/Hour';
import type { HourAsCard } from 'types/hour';
import { convertTimestampToPx } from 'utils/event';
import { fromISO } from 'utils/luxon-helpers';
import { Adjust } from './Adjust';
import styles from './ClickDragAdjust.module.scss';

export interface Range {
  index: number | null;
  from: string;
  to: string;
  color?: string | null;
  locked?: boolean;
  disabled?: boolean;
  live?: boolean;
  laneIndex?: number;
  hourId?: number;
  hourCard?: HourAsCard;
}

export interface AdjustRangeReturn {
  index: number | null;
  fromY: number;
  heightPx: number;
}

export type CalculatedRange = Range & AdjustRangeReturn;

export interface ClickDragReturn {
  selectionFromY: number;
  selectionToY: number;
}

interface Props {
  offsetTopPx: number;
  offsetLeftPx: number;
  adjustWidthPx: number | null;
  adjustMarginPx: number;
  adjustClassname?: string;
  selectionWidthPx: number;
  indicatorWidthPx: number;
  timelineStyle?: TimelineStyleTypes;
  pixelsPerMinute: number;
  /** "Today" in YYYY-MM-DD format, which may be different from startOfTimeline */
  currentDate: string;
  /** startOfTimeline is when the timeline draws from. Used mainly for calculating "when" based on pixels-from-top. */
  startOfTimeline: DateTime<true>;

  onClick: (id: number) => void;
  onChange: (range: Range) => void;
  onDelete: (range: Range) => void;
  onDragEnd: () => void;
  adjustableRanges: Array<Range>;
  /** Selection/resizing snaps to this number of minutes */
  snapToMinutes: number;
  showFromToLabels: boolean;
  timeFormat: 12 | 24;
  /** Rendered via TIC UI?  */
  hasEnabledTic?: boolean;
}

export const ClickDragAdjust: React.FC<Props> = props => {
  const {
    pixelsPerMinute = MINUTE_HEIGHT_PX,
    adjustableRanges = [],
    offsetTopPx = 0,
    offsetLeftPx = 0,
    selectionWidthPx = null,
    adjustWidthPx = null,
    snapToMinutes = 5,
    showFromToLabels = true,
    onClick,
    onChange,
    onDelete,
    onDragEnd,
    startOfTimeline,
    timeFormat,
    indicatorWidthPx,
    adjustMarginPx,
    adjustClassname,
    timelineStyle,
  } = props;

  const { pxBeforeMidnight } = useMemo(() => {
    const startOfDay = fromISO(props.currentDate).startOf('day');
    const minsBeforeMidnight = Math.max(0, startOfDay.diff(startOfTimeline, 'minutes').minutes);
    const pxBeforeMidnight = minsBeforeMidnight * pixelsPerMinute;
    return { pxBeforeMidnight };
  }, [props.currentDate, startOfTimeline, pixelsPerMinute]);

  const handleAdjustEnd = useCallback(
    ({ fromY, heightPx, index }: AdjustRangeReturn, range: Range) => {
      const adjustedFromPxToMinute = fromY / pixelsPerMinute;
      const minutesToAdd = heightPx / pixelsPerMinute;
      const from = startOfTimeline.plus({ minutes: adjustedFromPxToMinute });
      const to = from.plus({ minutes: minutesToAdd });

      onChange({
        index,
        hourId: range.hourId,
        from: from.toISO(),
        to: to.toISO(),
      });
    },
    [pixelsPerMinute, onChange, startOfTimeline]
  );

  const hasAdjustableHandles = !!adjustableRanges.length;

  const calculatedAdjustableRanges: Array<CalculatedRange> = useMemo(
    () =>
      adjustableRanges.map(range => {
        return {
          ...range,
          heightPx: convertTimestampToPx(range.to, range.from, 0, {
            pixelsPerMinute,
            hasMinimumHeight: !!range.live,
          }),
          fromY: convertTimestampToPx(range.from, startOfTimeline.toISO(), 0, {
            pixelsPerMinute,
          }),
        };
      }),
    [pixelsPerMinute, startOfTimeline, adjustableRanges]
  );

  return (
    <React.Fragment>
      {hasAdjustableHandles && (
        <React.Fragment>
          {calculatedAdjustableRanges.map(range => {
            const hasHourCard =
              timelineStyle === TimelineStyleTypes.timeEntriesOnTimeline && !!range.hourCard;

            const sizing = {
              offsetLeftPx: 0,
              wrapperOffsetLeftPx: 0,
              wrapperMarginLeftPx: 0,
            };

            if (
              timelineStyle === TimelineStyleTypes.timespansOnTimeline ||
              timelineStyle === TimelineStyleTypes.timeEntriesOnTimeline
            ) {
              const itemWidth = adjustWidthPx || selectionWidthPx || 0;
              const laneLeftPx = (itemWidth + adjustMarginPx) * (range.laneIndex || 0);
              sizing.wrapperMarginLeftPx = laneLeftPx + offsetLeftPx;
            }

            return (
              <Adjust
                {...sizing}
                className={adjustClassname}
                currentDate={props.currentDate}
                timeFormat={timeFormat}
                showFromToLabels={showFromToLabels && !hasHourCard}
                key={`${range.hourId}-${range.index}`}
                pixelsPerMinute={pixelsPerMinute}
                offsetTopPx={offsetTopPx}
                indicatorWidthPx={indicatorWidthPx || 0}
                selectionWidthPx={adjustWidthPx || selectionWidthPx || 0}
                range={range}
                onClick={() => onClick(range.hourId!)}
                onDelete={() => onDelete(range)}
                onChange={event => handleAdjustEnd(event, range)}
                onDragEnd={onDragEnd}
                snapToMinutes={snapToMinutes}
                resizeDirections={['top', 'bottom']}
                pxBeforeMidnight={pxBeforeMidnight}
                hasEnabledTic={props.hasEnabledTic}
              >
                {hasHourCard && (
                  <div className={styles.hourWrapper}>
                    <Hour hour={range.hourCard!} noMargin />
                  </div>
                )}
              </Adjust>
            );
          })}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};
ClickDragAdjust.displayName = 'ClickDragAdjust';
